import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { reportsService, ReportQueryParams, ReportResponse } from '../services/reports.service';
import { lookupService } from '../services/lookups.service';
import { useAuth } from '../context/AuthContext';
import ExcelJS from 'exceljs';

// Import our components from the barrel file
import {
  ReportHeader,
  JobsiteProgressFilters,
  JobsiteListByUnionFilters,
  JobsiteListByOrganizerFilters,
  MonthlyOrganizerActivityFilters,
  ReportExportButton,
  ReportEmptyState,
  ReportTable,
  ReportRecordCount,
  getReportColumns
} from '../components/reports';

// Import UI components
import { ReportDetailsSkeleton } from '../components/Skeleton';
import ReportsFilterOverlay from '../components/ReportsFilterOverlay';

// Define types
interface FilterState {
  sector: string[];
  province: string[];
  status: string[];
  startDate: string;
  endDate: string;
  search: string;
  month: string[];
  year: string[];
  organizer?: string[];
  unionStatus?: string[];
  localUnion?: string[];
  type?: string[];
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc' | null;
}

// Extract data formatting functions for better testability
const formatAddressValue = (value: any): string => {
  if (!value) return '-';
  return `${value.address2 ? value.address2 + ' - ' : ''} ${value.address1}, ${value.city}, ${value.postalCode}`;
};

const formatDateValue = (value: string): string => {
  return value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-';
};

const formatArrayValue = (value: any[]): string => {
  if (!value || value.length === 0) return '-';
  return value.join('; ');
};
const formatLocalUnionsValue = (value: any[]): string => {
  if (!value || value.length === 0) return '-';
  return value.map((localUnion) => `${localUnion.code} - ${localUnion.unionNumber}`).join(', ') || '-';
};

const formatCellValue = (value: any, accessor: string): string => {
  if (accessor === 'address') {
    return formatAddressValue(value);
  }
  if (accessor === 'localUnions') {
    return formatLocalUnionsValue(value);
  }
  if (Array.isArray(value)) {
    return formatArrayValue(value);
  }
  if (accessor === 'startDate' || accessor === 'endDate' || accessor === 'lastUpdated' || accessor === 'visitDate' || accessor === 'dateOfVisit') {
    return formatDateValue(value);
  }

  return value || '-';
};

// Export to Excel function
const exportToExcel = async (data: any[], reportType: string, userRole?: string | null) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(reportType);

  // Get columns based on report type and user role, excluding Actions column
  const columns = getReportColumns(reportType, userRole).filter(col => col.accessor !== 'actions');

  // Add headers
  const headers = columns.map(col => col.label);
  worksheet.addRow(headers);

  // Add data
  data.forEach(item => {
    const rowData = columns.map(col => {
      return formatCellValue(item[col.accessor], col.accessor);
    });
    worksheet.addRow(rowData);
  });

  // Style header row (replicated from jobsite-visits.tsx)
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE2E2E2' }
  };

  // Auto-size columns (replicated from jobsite-visits.tsx)
  worksheet.columns.forEach((column: Partial<ExcelJS.Column>) => {
    let maxLength = 0;
    if (column && column.eachCell) {
      column.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength;
    }
  });

  // Generate blob
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${reportType.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
};

// Extract organizer data transformation function to top level for better testability
const transformOrganizerData = (item: any) => ({
    ...item,
    organizerName: item.organizerName || '',
    dateOfVisit: item.dateOfVisit || ''
});

// Extract lookup data fetching logic into separate functions for better testability
const formatSectorsData = (sectorsData: any[]) => {
  return sectorsData.map(sector => ({
    id: sector.id.toString(),
    name: sector.name
  }));
};

const formatProvincesData = (provincesData: any[]) => {
  return provincesData.map(province => ({
    id: province.id.toString(),
    name: province.name
  }));
};

const formatLocalUnionsData = (localsData: any[]) => {
  return localsData.map(local => ({
    id: local.id.toString(),
    name: `Local ${local.unionNumber} - ${local.province.name}`
  }));
};

const formatOrganizersData = (organizersData: any[]) => {
  return organizersData.map(organizer => ({
    id: organizer.id.toString(),
    name: organizer.name,
    role: {
      id: organizer.role.id,
      roleName: organizer.role.roleName,
    }
  }));
};

// Extract the year options generator function for better testability (fallback)
const generateYearOptions = (): { id: string; name: string }[] => {
    const currentYear = new Date().getFullYear();
    return [
        { id: currentYear.toString(), name: currentYear.toString() }
    ];
};

// Function to fetch available years from backend
const fetchAvailableYears = async (): Promise<{ id: string; name: string }[]> => {
    try {
        return await reportsService.getAvailableYears();
    } catch (error) {
        console.error('Error fetching available years:', error);
        // Fallback to current year if fetch fails
        return generateYearOptions();
    }
};

// Extract the main lookup data fetching function for better testability
const fetchAllLookupData = async (reportType: string | undefined) => {
  const sectorsData = await lookupService.getSectors();
  const formattedSectors = formatSectorsData(sectorsData);

  const provincesData = await lookupService.getProvinces();
  const formattedProvinces = formatProvincesData(provincesData);
  const localsData = await lookupService.getLocals();
  const formattedLocals = formatLocalUnionsData(localsData);

//   let formattedLocals: { id: string; name: string }[] = [];
  let formattedOrganizers: { id: string; name: string; role: { id: string; roleName: string } }[] = [];

  // Fetch organizers data when needed
  if (reportType === 'Jobsite List Report by Organizer' || reportType === 'Monthly Organizer Activity Report') {
    const organizersData = await lookupService.getOrganizers();
    formattedOrganizers = formatOrganizersData(organizersData);
  }

  return {
    sectors: formattedSectors,
    provinces: formattedProvinces,
    localUnions: formattedLocals,
    organizers: formattedOrganizers
  };
};

// Extract report data fetching logic for better testability
const fetchReportByType = async (reportType: string, params: ReportQueryParams): Promise<ReportResponse> => {
  if (reportType === 'Monthly Organizer Activity Report') {
    return await reportsService.getOrganizerMonthlyActivity(params);
  } else if (reportType === 'Jobsite Progress Report') {
    return await reportsService.getJobSiteProgressReport(params);
  } else if (reportType === 'Jobsite List by Union') {
    return await reportsService.getJobSitesByUnion(params);
  } else if (reportType === 'Jobsite List Report by Organizer') {
    return await reportsService.getOrganizerJobsites(params);
  } else {
    throw new Error('Unsupported report type');
  }
};

// Extract the main report data processing function for better testability
const processReportData = async (
  reportType: string,
  params: ReportQueryParams,
  filtersApplied: boolean
): Promise<{ data: any[]; recordCount: number }> => {
  if (!filtersApplied) {
    return { data: [], recordCount: 0 };
  }

  const response = await fetchReportByType(reportType, params);
  let processedData = response.data;
  if (reportType === 'Monthly Organizer Activity Report') {
    processedData = response.data.map(transformOrganizerData);
  }

  return { data: processedData, recordCount: response.data.length };
};

// Extract lookup data handler for better testability
const createLookupDataHandler = (
    setSectors: (sectors: { id: string; name: string }[]) => void,
    setProvinces: (provinces: { id: string; name: string }[]) => void,
    setLocalUnions: (localUnions: { id: string; name: string }[]) => void,
    setOrganizers: (organizers: { id: string; name: string; role: { id: string; roleName: string } }[]) => void,
    reportType: string | undefined
) => async () => {
    try {
        const lookupData = await fetchAllLookupData(reportType);
        setSectors(lookupData.sectors);
        setProvinces(lookupData.provinces);
        setLocalUnions(lookupData.localUnions);
        setOrganizers(lookupData.organizers);
    } catch (error) {
        console.error('Error fetching lookup data:', error);
    }
};

// Extract report data handler for better testability
const createReportDataHandler = (
    setLoading: (loading: boolean) => void,
    setReportData: (data: any[]) => void,
    setError: (error: string | null) => void,
    activeFilters: FilterState,
    reportType: string | undefined,
    sortConfig: SortConfig,
    filtersApplied: boolean
) => async () => {
    try {
        setLoading(true);
        const params = buildReportParams(activeFilters, reportType, sortConfig);
        const result = await processReportData(reportType || '', params, filtersApplied);
        
        setReportData(result.data);
        
    } catch (err) {
        setError(`Failed to fetch ${reportType || 'report'}`);
        console.error('Error fetching report:', err);
        setReportData([]);
    } finally {
        setLoading(false);
    }
};

// Extract parameter building function for better testability
const buildReportParams = (filters: FilterState, reportType: string | undefined, sort: SortConfig): ReportQueryParams => {
    const params: ReportQueryParams = {
        sector: filters.sector,
        status: filters.status,
        province: filters.province,
        localUnion: filters.localUnion,
        organizer: filters.organizer,
        unionStatus: filters.unionStatus
    };

    // Add month and year for Monthly Organizer Activity Report
    if (reportType === 'Monthly Organizer Activity Report') {
        if (filters.month && filters.month.length > 0) {
            // Convert month values to numbers
            params.month = filters.month.map(month => parseInt(month, 10));
        }
        if (filters.year && filters.year.length > 0) {
            // Convert year values to numbers - support multiple years
            params.year = filters.year.map(year => parseInt(year, 10));
        }
        if (filters.type && filters.type.length > 0) {
            params.type = filters.type;
        }
    } else {
        // For other reports, use start and end dates
        params.startDate = filters.startDate || undefined;
        params.endDate = filters.endDate || undefined;
    }

    // Add sorting parameters if a sort direction is specified
    if (sort.direction) {
        params.sortBy = sort.key;
        params.sortOrder = sort.direction;
    }
    
    return params;
};

// Create default/empty filters object for better testability
const createEmptyFilters = (searchTerm: string): FilterState => ({
    sector: [],
    province: [],
    status: [],
    startDate: '',
    endDate: '',
    search: searchTerm,
    localUnion: [],
    organizer: [],
    unionStatus: [],
    month: [],
    year: [],
    type: []
});

// Extract filter close handler for better testability
const createFilterCloseHandler = (setIsFilterOverlayOpen: (value: boolean) => void) => () => {
    setIsFilterOverlayOpen(false);
};

// Extract filter clear all handler for better testability
const createFilterClearAllHandler = (
    setActiveFilters: (filters: FilterState) => void,
    setFiltersApplied: (applied: boolean) => void,
    setReportData: (data: any[]) => void,
    setLoading: (loading: boolean) => void,
    searchTerm: string
) => () => {
    setActiveFilters(createEmptyFilters(searchTerm));
    setFiltersApplied(false);
    setReportData([]);
    setLoading(false);
};

// Extract the filter validation logic for better testability
const checkFiltersApplied = (filters: FilterState, reportType: string | undefined): boolean => {
    return filters.sector.length > 0 ||
        filters.province.length > 0 ||
        filters.status.length > 0 ||
        (filters.localUnion && filters.localUnion.length > 0) ||
        (filters.organizer && filters.organizer.length > 0) ||
        (filters.unionStatus && filters.unionStatus.length > 0) ||
        (reportType === 'Monthly Organizer Activity Report'
            ? ((filters.month && filters.month.length > 0) &&
                (filters.year && filters.year.length > 0))
            : (filters.startDate !== '' || filters.endDate !== '')) ||
        filters.search !== '';
};

// Extract filter apply handler for better testability
const createFilterApplyHandler = (
    setActiveFilters: (filters: FilterState) => void,
    setFiltersApplied: (applied: boolean) => void,
    reportType: string | undefined
) => (filters: FilterState) => {
    setActiveFilters(filters);
    const hasAppliedFilters = checkFiltersApplied(filters, reportType);
    setFiltersApplied(hasAppliedFilters);
};

// Extract sort config creation for better testability
const createSortConfig = (key: string, direction: 'asc' | 'desc' | null): SortConfig => ({
    key,
    direction
});

// Extract sort handler for better testability
const createSortHandler = (
    setSortConfig: (config: SortConfig) => void,
    fetchReportData: () => void,
    filtersApplied: boolean
) => (key: string, direction: 'asc' | 'desc' | null) => {
    setSortConfig(createSortConfig(key, direction));
    // Re-fetch data with sorting if filters are applied
    if (filtersApplied) {
        fetchReportData();
    }
};

// Extract export function for better testability
const exportToPdf = async (data: any[], reportType: string, activeFilters?: FilterState, lookupData?: {
    sectors: { id: string; name: string }[];
    provinces: { id: string; name: string }[];
    localUnions: { id: string; name: string }[];
    organizers: { id: string; name: string }[];
    statusOptions: { id: string; name: string }[];
    unionStatusOptions: { id: string; name: string }[];
    monthOptions: { id: string; name: string }[];
    yearOptions: { id: string; name: string }[];
}, userRole?: string | null) => {
    const jsPDF = require('jspdf').default;
    const doc = new jsPDF('landscape');

    // Get columns based on report type and user role, excluding Actions column
    const columns = getReportColumns(reportType, userRole).filter(col => col.accessor !== 'actions');

    // Calculate dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const lineHeight = 5;
    const cellPadding = 2; // Added padding for cells
    const footerHeight = 15; // Space reserved for footer
    const maxContentHeight = pageHeight - 2 * margin - footerHeight;
    const columnsCount = columns.length;
    const columnWidth = (pageWidth - 2 * margin) / columnsCount;

    // Helper function to draw cell borders
    const drawCellBorders = (x: number, y: number, width: number, height: number) => {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.1);
        // Draw rectangle for cell, accounting for padding
        doc.rect(x, y - (cellPadding / 2), width, height);
    };

    // Helper function to wrap text and return height
    const splitTextToSize = (text: string, width: number): { lines: string[]; height: number } => {
        if (!text || text === '-') return { lines: [text], height: lineHeight + (2 * cellPadding) };
        const textStr = text.toString();
        doc.setFontSize(8);
        const lines = doc.setFont("helvetica").splitTextToSize(textStr, width - (2 * cellPadding));
        return { 
            lines, 
            height: (lines.length * lineHeight) + (2 * cellPadding) // Add padding to height
        };
    };

    // Helper function to format and display filters
    const formatAndAddFilters = (yPosition: number): number => {
        if (!activeFilters || !lookupData) return yPosition;

        const filterParts: string[] = [];

        // Helper function to get names by IDs
        const getNamesByIds = (ids: string[], options: { id: string; name: string }[]): string[] => {
            return ids.map(id => {
                const found = options.find(option => option.id === id);
                return found ? found.name : id;
            }).filter(Boolean);
        };

        // Process each filter type
        if (activeFilters.sector?.length > 0) {
            const names = getNamesByIds(activeFilters.sector, lookupData.sectors);
            if (names.length > 0) {
                filterParts.push(`Sectors: ${names.join(', ')}`);
            }
        }

        if (activeFilters.province?.length > 0) {
            const names = getNamesByIds(activeFilters.province, lookupData.provinces);
            if (names.length > 0) {
                filterParts.push(`Provinces: ${names.join(', ')}`);
            }
        }

        if (activeFilters.status?.length > 0) {
            const names = getNamesByIds(activeFilters.status, lookupData.statusOptions);
            if (names.length > 0) {
                filterParts.push(`Status: ${names.join(', ')}`);
            }
        }

        if (activeFilters.localUnion && activeFilters.localUnion.length > 0) {
            const names = getNamesByIds(activeFilters.localUnion, lookupData.localUnions);
            if (names.length > 0) {
                filterParts.push(`Local Unions: ${names.join(', ')}`);
            }
        }

        if (activeFilters.organizer && activeFilters.organizer.length > 0) {
            const names = getNamesByIds(activeFilters.organizer, lookupData.organizers);
            if (names.length > 0) {
                filterParts.push(`Organizers: ${names.join(', ')}`);
            }
        }

        if (activeFilters.unionStatus && activeFilters.unionStatus.length > 0) {
            const names = getNamesByIds(activeFilters.unionStatus, lookupData.unionStatusOptions);
            if (names.length > 0) {
                filterParts.push(`Union Status: ${names.join(', ')}`);
            }
        }

        if (activeFilters.month?.length > 0) {
            const names = getNamesByIds(activeFilters.month, lookupData.monthOptions);
            if (names.length > 0) {
                filterParts.push(`Months: ${names.join(', ')}`);
            }
        }

        if (activeFilters.year?.length > 0) {
            const names = getNamesByIds(activeFilters.year, lookupData.yearOptions);
            if (names.length > 0) {
                filterParts.push(`Years: ${names.join(', ')}`);
            }
        }

        if (activeFilters.startDate) {
            filterParts.push(`Start Date: ${new Date(activeFilters.startDate).toLocaleDateString()}`);
        }

        if (activeFilters.endDate) {
            filterParts.push(`End Date: ${new Date(activeFilters.endDate).toLocaleDateString()}`);
        }

        if (activeFilters.search) {
            filterParts.push(`Search: ${activeFilters.search}`);
        }

        // Only add filters section if there are filters to show
        if (filterParts.length === 0) return yPosition;

        // Add "Applied Filters:" header
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Applied Filters:', margin, yPosition);
        yPosition += 6;

        // Set font size for filter content
        doc.setFontSize(9);

        // Add each filter on a new line with bold headings
        filterParts.forEach(filterText => {
            // Split the filter text to separate label from values
            const colonIndex = filterText.indexOf(':');
            if (colonIndex !== -1) {
                const label = filterText.substring(0, colonIndex + 1); // Include the colon
                const values = filterText.substring(colonIndex + 1);
                
                // Draw the label in bold
                doc.setFont('helvetica', 'bold');
                const labelWidth = doc.getStringUnitWidth(label) * doc.internal.scaleFactor;
                doc.text(label, margin, yPosition);
                
                // Draw the values in normal font
                doc.setFont('helvetica', 'normal');
                
                // Calculate available width for values (subtract margins and label width)
                const availableWidth = pageWidth - (2 * margin) - labelWidth - 2; // 2 for small spacing
                
                // Split values text to fit within available width
                const valueLines = doc.splitTextToSize(values, availableWidth);
                
                // Position the first value line next to the label
                let valueX = margin + labelWidth + 2;
                doc.text(valueLines[0] || '', valueX, yPosition);
                yPosition += 4;
                
                // If there are additional lines, add them with proper indentation
                if (valueLines.length > 1) {
                    for (let i = 1; i < valueLines.length; i++) {
                        doc.text(valueLines[i], valueX, yPosition);
                        yPosition += 4;
                    }
                }
            } else {
                // Fallback: if no colon found, just add the text normally
                doc.setFont('helvetica', 'normal');
                doc.text(filterText, margin, yPosition);
                yPosition += 4;
            }
        });
        
        // Add some spacing after filters
        yPosition += 4;
        
        return yPosition;
    };

    // Helper function to add footer with generated date
    const addFooter = () => {
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        const formattedTime = currentDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100); // Gray color for footer
        
        const footerText = `Generated on ${formattedDate} at ${formattedTime}`;
        const footerY = pageHeight - margin;
        
        // Center the footer text
        const textWidth = doc.getStringUnitWidth(footerText) * doc.internal.scaleFactor;
        const textX = (pageWidth - textWidth) / 2;
        
        doc.text(footerText, textX, footerY);
        doc.setTextColor(0, 0, 0); // Reset to black
    };
    
    // Add title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(reportType, margin, margin + 4);

    // Add organizer name for MOAR
    let yPos = margin + 10;
    if (reportType === 'Monthly Organizer Activity Report' && activeFilters && lookupData) {
        // Get organizer name(s) from filters
        let organizerNames: string[] = [];
        if (activeFilters.organizer && activeFilters.organizer.length > 0) {
            organizerNames = activeFilters.organizer.map(id => {
                const organizer = lookupData.organizers.find(org => org.id === id);
                return organizer ? organizer.name : '';
            }).filter(Boolean);
        } else if (data && data.length > 0 && data[0].organizerName) {
            // Fallback: extract from data if not in filters
            const uniqueOrganizers = Array.from(new Set(data.map(item => item.organizerName).filter(Boolean)));
            organizerNames = uniqueOrganizers;
        }

        if (organizerNames.length > 0) {
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            const organizerText = organizerNames.length === 1
                ? `Organizer: ${organizerNames[0]}`
                : `Organizers: ${organizerNames.join(', ')}`;
            doc.text(organizerText, margin, yPos);
            yPos += 7;
        }
    }

    // Add filters section after the title and organizer
    yPos = formatAndAddFilters(yPos);
    
    // Add headers
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    
    // Calculate header height first
    let headerHeight = lineHeight;
    columns.forEach(col => {
        const { height } = splitTextToSize(col.label, columnWidth);
        headerHeight = Math.max(headerHeight, height);
    });
    
    // Draw header row background
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - (cellPadding / 2), pageWidth - (2 * margin), headerHeight, 'F');
    
    // Draw header cells
    columns.forEach((col, i) => {
        const x = margin + (i * columnWidth);
        const { lines } = splitTextToSize(col.label, columnWidth);
        
        // Draw header cell border
        drawCellBorders(x, yPos, columnWidth, headerHeight);
        
        // Draw header text with padding
        lines.forEach((line: string, lineIndex: number) => {
            doc.text(line, x + cellPadding, yPos + (lineIndex * lineHeight) + cellPadding);
        });
    });
    
    // Move to start of data rows
    yPos += headerHeight;
    
    // Add footer to the first page with headers
    addFooter();
    
    // Add data rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    // Process each data row
    data.forEach((item, rowIndex) => {
        // First pass: calculate row height
        let rowHeight = lineHeight;
        const rowContent: Array<{ x: number; text: string[]; }> = [];

        columns.forEach((col, colIndex) => {
            const x = margin + (colIndex * columnWidth);
            const value = formatCellValue(item[col.accessor], col.accessor);
            const { lines, height } = splitTextToSize(value, columnWidth);
            rowContent.push({ x, text: lines });
            rowHeight = Math.max(rowHeight, height);
        });

        // Check if we need a new page
        if (yPos + rowHeight > maxContentHeight) {
            // Add footer to current page before creating new page
            addFooter();
            
            doc.addPage('landscape');
            yPos = margin + 12;
            
            // Repeat headers on new page
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(240, 240, 240);
            doc.rect(margin, yPos - (cellPadding / 2), pageWidth - (2 * margin), headerHeight, 'F');
            
            columns.forEach((col, i) => {
                const x = margin + (i * columnWidth);
                const { lines } = splitTextToSize(col.label, columnWidth);
                
                // Draw header cell border
                drawCellBorders(x, yPos, columnWidth, headerHeight);
                
                // Draw header text with padding
                lines.forEach((line: string, lineIndex: number) => {
                    doc.text(line, x + cellPadding, yPos + (lineIndex * lineHeight) + cellPadding);
                });
            });
            
            doc.setFont('helvetica', 'normal');
            yPos += headerHeight;
        }

        // Add subtle alternating row background
        if (rowIndex % 2 === 1) {
            doc.setFillColor(248, 248, 248);
            doc.rect(margin, yPos - (cellPadding / 2), pageWidth - (2 * margin), rowHeight, 'F');
        }

        // Draw all cells in the row
        rowContent.forEach((cell, colIndex) => {
            // Draw cell border
            drawCellBorders(cell.x, yPos, columnWidth, rowHeight);
            
            // Draw cell content with padding
            cell.text.forEach((line: string, lineIndex: number) => {
                doc.text(line, cell.x + cellPadding, yPos + (lineIndex * lineHeight) + cellPadding);
            });
        });

        // Move to next row position
        yPos += rowHeight;
    });
    
    // Add footer to the last page
    addFooter();
    
    // Generate and download the file
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `${reportType.replace(/\s+/g, '_')}_${currentDate}.pdf`;
    doc.save(filename);
};

const createExportHandler = (
    reportData: any[],
    reportType: string,
    activeFilters: FilterState,
    lookupData: {
        sectors: { id: string; name: string }[];
        provinces: { id: string; name: string }[];
        localUnions: { id: string; name: string }[];
        organizers: { id: string; name: string }[];
        statusOptions: { id: string; name: string }[];
        unionStatusOptions: { id: string; name: string }[];
        monthOptions: { id: string; name: string }[];
        yearOptions: { id: string; name: string }[];
    },
    userRole?: string | null
) => ({
    handleExcelExport: () => exportToExcel(reportData, reportType, userRole),
    handlePdfExport: () => exportToPdf(reportData, reportType, activeFilters, lookupData, userRole)
});

// Extract navigation handler for better testability
const createBackHandler = (navigate: (path: string) => void) => () => {
    navigate('/reports');
};

// Extract apply filters handler for better testability
const createApplyFiltersHandler = (
    setFiltersApplied: (applied: boolean) => void,
    setLoading: (loading: boolean) => void,
    setReportData: (data: any[]) => void,
    setError: (error: string | null) => void,
    activeFilters: FilterState,
    reportType: string | undefined,
    sortConfig: SortConfig
) => async () => {
    setFiltersApplied(true);
    setLoading(true);
    setError(null);
    
    // Start the 3-second minimum loading period and fetch data simultaneously
    const startTime = Date.now();
    const minLoadingTime = 3000; // 3 seconds
    
    try {
        // Build params and fetch data
        const params = buildReportParams(activeFilters, reportType, sortConfig);
        const result = await processReportData(reportType || '', params, true);
        
        // Calculate remaining time to reach minimum 3 seconds
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minLoadingTime - elapsedTime;
        
        // If less than 3 seconds have passed, wait for the remaining time
        if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        setReportData(result.data);
        
    } catch (error) {
        // Even if there's an error, ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minLoadingTime - elapsedTime;
        
        if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        setError(`Failed to fetch ${reportType || 'report'}`);
        console.error('Error in apply filters:', error);
        setReportData([]);
    } finally {
        setLoading(false);
    }
};

// Extract filter validation functions with explicit parameters for better testability
const validateJobsiteProgressFilters = (filters: FilterState): boolean => {
    return Boolean(
        (filters.startDate || filters.endDate) ||
        filters.sector.length > 0 ||
        filters.province.length > 0 ||
        filters.status.length > 0
    );
};

const validateJobsiteListByUnionFilters = (filters: FilterState): boolean => {
    return Boolean(
        (filters.startDate || filters.endDate) ||
        (filters.unionStatus && filters.unionStatus.length > 0) ||
        filters.province.length > 0 ||
        filters.status.length > 0
    );
};

const validateJobsiteListByOrganizerFilters = (filters: FilterState): boolean => {
    return Boolean(
        (filters.startDate || filters.endDate) ||
        (filters.organizer && filters.organizer.length > 0) ||
        filters.status.length > 0 ||
        filters.province.length > 0 ||
        (filters.unionStatus && filters.unionStatus.length > 0)
    );
};

const validateMonthlyOrganizerActivityFilters = (filters: FilterState): boolean => {
    return Boolean(
        (filters.month && filters.month.length > 0) &&
        (filters.year && filters.year.length > 0)
    );
};

// Extract filter validation wrapper creators for better testability
const createJobsiteProgressFiltersValidator = (activeFilters: FilterState) => (): boolean => {
    return validateJobsiteProgressFilters(activeFilters);
};

const createJobsiteListByUnionFiltersValidator = (activeFilters: FilterState) => (): boolean => {
    return validateJobsiteListByUnionFilters(activeFilters);
};

const createJobsiteListByOrganizerFiltersValidator = (activeFilters: FilterState) => (): boolean => {
    return validateJobsiteListByOrganizerFilters(activeFilters);
};

const createMonthlyOrganizerActivityFiltersValidator = (activeFilters: FilterState) => (): boolean => {
    return validateMonthlyOrganizerActivityFilters(activeFilters);
};

const ReportDetails: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentRole } = useAuth();
    const [searchTerm] = useState('');
    const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState(false);
    const [reportData, setReportData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [activeFilters, setActiveFilters] = useState<FilterState>({
        sector: [],
        province: [],
        status: [],
        startDate: '',
        endDate: '',
        search: '',
        month: [],
        year: []
    });
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: 'jobSiteName',
        direction: null
    });

    // Add state for sectors and provinces
    const [sectors, setSectors] = useState<{ id: string; name: string }[]>([]);
    const [provinces, setProvinces] = useState<{ id: string; name: string }[]>([]);
    const [localUnions, setLocalUnions] = useState<{ id: string; name: string }[]>([]);
    const [organizers, setOrganizers] = useState<{ id: string; name: string; role: { id: string; roleName: string } }[]>([]);
    const [statusOptions] = useState<{ id: string; name: string }[]>([
        { id: 'active', name: 'Active' },
        { id: 'inactive', name: 'Inactive' },
        { id: 'completed', name: 'Completed' }
    ]);

    const [unionStatusOptions] = useState<{ id: string; name: string }[]>([
        { id: 'union', name: 'Union' },
        { id: 'non-union', name: 'Non-Union' },
        { id: 'mixed', name: 'Mixed' }
    ]);

    // Add month and year options
    const [monthOptions] = useState([
        { id: '01', name: 'January' },
        { id: '02', name: 'February' },
        { id: '03', name: 'March' },
        { id: '04', name: 'April' },
        { id: '05', name: 'May' },
        { id: '06', name: 'June' },
        { id: '07', name: 'July' },
        { id: '08', name: 'August' },
        { id: '09', name: 'September' },
        { id: '10', name: 'October' },
        { id: '11', name: 'November' },
        { id: '12', name: 'December' }
    ]);

    const [yearOptions, setYearOptions] = useState<{ id: string; name: string }[]>(generateYearOptions());

    // Create handlers using the extracted functions
    const fetchLookupData = createLookupDataHandler(
        setSectors,
        setProvinces,
        setLocalUnions,
        setOrganizers,
        location.state?.reportType
    );

    const fetchReportData = createReportDataHandler(
        setLoading,
        setReportData,
        setError,
        activeFilters,
        location.state?.reportType,
        sortConfig,
        filtersApplied
    );

    useEffect(() => {
        fetchLookupData();
    }, [location.state?.reportType]);

    useEffect(() => {
        // Fetch available years on mount
        const loadYears = async () => {
            const years = await fetchAvailableYears();
            setYearOptions(years);
        };
        loadYears();
    }, []);

    useEffect(() => {
        fetchReportData();
    }, [filtersApplied, sortConfig]);

    // Auto-refresh when all filters are manually cleared after being applied
    useEffect(() => {
        // Only trigger auto-refresh if filters were previously applied
        if (filtersApplied) {
            const hasAnyFilters = checkFiltersApplied(activeFilters, location.state?.reportType);
            
            // If no filters are applied anymore, automatically clear the data
            if (!hasAnyFilters) {
                setFiltersApplied(false);
                setReportData([]);
                setLoading(false);
                
            }
        }
    }, [activeFilters, filtersApplied, location.state?.reportType]);

    const handleFilterClose = createFilterCloseHandler(setIsFilterOverlayOpen);

    const handleFilterClearAll = createFilterClearAllHandler(
        setActiveFilters,
        setFiltersApplied,
        setReportData,
        setLoading,
        searchTerm
    );
    
    const handleFilterApply = createFilterApplyHandler(
        setActiveFilters,
        setFiltersApplied,
        location.state?.reportType
    );
    
    const handleSort = createSortHandler(setSortConfig, fetchReportData, filtersApplied);

    // Extract export functions for better testability
    const { handleExcelExport, handlePdfExport } = createExportHandler(
        reportData,
        location.state?.reportType || '',
        activeFilters,
        {
            sectors,
            provinces,
            localUnions,
            organizers,
            statusOptions,
            unionStatusOptions,
            monthOptions,
            yearOptions
        },
        currentRole
    );

    // Extract navigation function for better testability
    const onBack = createBackHandler(navigate);

    // Extract apply filters function for better testability
    const handleApplyFilters = createApplyFiltersHandler(
        setFiltersApplied,
        setLoading,
        setReportData,
        setError,
        activeFilters,
        location.state?.reportType,
        sortConfig
    );
    
    // Create wrapper functions that use the current state
    const isJobsiteProgressFiltersValid = createJobsiteProgressFiltersValidator(activeFilters);

    const isJobsiteListByUnionFiltersValid = createJobsiteListByUnionFiltersValidator(activeFilters);

    const isJobsiteListByOrganizerFiltersValid = createJobsiteListByOrganizerFiltersValidator(activeFilters);

    const isMonthlyOrganizerActivityFiltersValid = createMonthlyOrganizerActivityFiltersValidator(activeFilters);

    return (
        <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
            <div className="flex justify-between mb-4 sm:mb-6">
                <ReportHeader 
                  title={location.state?.reportType || "Sample Report Name"} 
                  onBack={onBack} 
                />
            </div>

            {location.state?.reportType === 'Jobsite Progress Report' && (
                <JobsiteProgressFilters
                    activeFilters={activeFilters as any}
                    setActiveFilters={setActiveFilters as any}
                    sectors={sectors}
                    provinces={provinces}
                    localUnions={localUnions}
                    statusOptions={statusOptions}
                    handleApplyFilters={handleApplyFilters}
                    handleFilterClearAll={handleFilterClearAll}
                    isJobsiteProgressFiltersValid={isJobsiteProgressFiltersValid}
                    setLoading={setLoading}
                />
            )}

            {location.state?.reportType === 'Jobsite List by Union' && (
                <JobsiteListByUnionFilters
                    activeFilters={activeFilters as any}
                    setActiveFilters={setActiveFilters as any}
                    provinces={provinces}
                    localUnions={localUnions}
                    statusOptions={statusOptions}
                    unionStatusOptions={unionStatusOptions}
                    handleApplyFilters={handleApplyFilters}
                    handleFilterClearAll={handleFilterClearAll}
                    isJobsiteListByUnionFiltersValid={isJobsiteListByUnionFiltersValid}
                    setLoading={setLoading}
                />
            )}

            {location.state?.reportType === 'Jobsite List Report by Organizer' && (
                <JobsiteListByOrganizerFilters
                    activeFilters={activeFilters as any}
                    setActiveFilters={setActiveFilters as any}
                    localUnions={localUnions}
                    provinces={provinces}
                    statusOptions={statusOptions}
                    unionStatusOptions={unionStatusOptions}
                    organizers={organizers}
                    handleApplyFilters={handleApplyFilters}
                    handleFilterClearAll={handleFilterClearAll}
                    isJobsiteListByOrganizerFiltersValid={isJobsiteListByOrganizerFiltersValid}
                    setLoading={setLoading}
                />
            )}

            {location.state?.reportType === 'Monthly Organizer Activity Report' && (
                <MonthlyOrganizerActivityFilters
                    activeFilters={activeFilters as any}
                    setActiveFilters={setActiveFilters as any}
                    provinces={provinces}
                    localUnions={localUnions}
                    unionStatusOptions={unionStatusOptions}
                    organizers={organizers}
                    monthOptions={monthOptions}
                    yearOptions={yearOptions}
                    handleApplyFilters={handleApplyFilters}
                    handleFilterClearAll={handleFilterClearAll}
                    isMonthlyOrganizerActivityFiltersValid={isMonthlyOrganizerActivityFiltersValid}
                    setLoading={setLoading}
                    userRole={currentRole}
                />
            )}

            {loading ? (
                <ReportDetailsSkeleton />
            ) : (
                <>
                    {reportData.length > 0 && filtersApplied && (
                        <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
                            <div className="flex items-center justify-between">
                                <div className="text-gray-600">
                                </div>
                                <ReportExportButton onExcelExport={handleExcelExport} onPdfExport={handlePdfExport} />
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
                        <div className="overflow-x-auto">
                            {error ? (
                                <div className="text-red-500 text-center p-4">{error}</div>
                            ) : !filtersApplied ? (
                                <ReportEmptyState />
                            ) : reportData.length === 0 ? (
                                <div className="text-gray-500 text-center p-4">No data available for the selected filters</div>
                            ) : (
                                <ReportTable
                                    data={reportData}
                                    columns={getReportColumns(location.state?.reportType || '', currentRole)}
                                    onSort={handleSort}
                                    sortConfig={sortConfig}
                                    onRowClick={location.state?.reportType === 'Monthly Organizer Activity Report'
                                        ? (row) => {
                                            if (row.type === 'Jobsite Visit' && row.visitId) {
                                                navigate(`/jobsite-management/visit-details/${row.visitId}`);
                                            } else if (row.jobId) {
                                                navigate(`/jobsite-management/details/${row.jobId}`);
                                            }
                                        }
                                        : undefined
                                    }
                                />
                            )}
                        </div>

                        {reportData.length > 0 && filtersApplied && (
                            <ReportRecordCount count={reportData.length} />
                        )}
                    </div>

                    <ReportsFilterOverlay
                        isOpen={isFilterOverlayOpen}
                        onClose={handleFilterClose}
                        onClearAll={handleFilterClearAll}
                        activeFilters={activeFilters}
                        onApply={handleFilterApply as any}
                    />
                </>
            )}
        </div>
    );
};

export default ReportDetails;

// Export all extracted functions for testing
export {
    formatAddressValue,
    formatDateValue,
    formatArrayValue,
    formatCellValue,
    exportToExcel,
    transformOrganizerData,
    formatSectorsData,
    formatProvincesData,
    formatLocalUnionsData,
    formatOrganizersData,
    fetchAllLookupData,
    fetchReportByType,
    processReportData,
    createLookupDataHandler,
    createReportDataHandler,
    buildReportParams,
    createEmptyFilters,
    createFilterCloseHandler,
    createFilterClearAllHandler,
    checkFiltersApplied,
    createFilterApplyHandler,
    createSortConfig,
    createSortHandler,
    createExportHandler,
    createBackHandler,
    createApplyFiltersHandler,
    validateJobsiteProgressFilters,
    validateJobsiteListByUnionFilters,
    validateJobsiteListByOrganizerFilters,
    validateMonthlyOrganizerActivityFilters,
    createJobsiteProgressFiltersValidator,
    createJobsiteListByUnionFiltersValidator,
    createJobsiteListByOrganizerFiltersValidator,
    createMonthlyOrganizerActivityFiltersValidator,
    generateYearOptions,
    fetchAvailableYears,
    exportToPdf
}; 
